import { NewButtonVariant } from "@visiblethread/react-components";
import { getFilterDescription } from "constants/discovery";
import { modalTypes } from "constants/types";
import { has } from "lodash";
import React from "react";
import { logout } from "slices/app/actions/app";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  deleteDictionary,
  setDefaultDictionary,
} from "slices/dictionary/actions/dictionary";
import { setSearchTerm } from "slices/discovery/discoverySlice";
import { editItem } from "slices/settings/slices/acronymIgnoreSlice";
import { systemAdminRemoveUserFromWorkspace } from "slices/systemAdmin/actions/systemAdmin";
import {
  deleteUser,
  exportResultsImportUsersFromCsv,
} from "slices/systemAdmin/actions/users";
import {
  exportResultFromImportUsers,
  userAdminRemoveUserFromWorkspace,
} from "slices/userAdmin/actions/userAdmin";
import { pluralize } from "utils/string";

export const searchWarningConfirmationModal = (message, dispatch) => ({
  headerText: "Warning",
  bodyText: message,
  okButton: {
    text: "Confirm",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const systemAdminRemoveMemberModal = (
  userName,
  workspaceName,
  workspaceId,
  dispatch
) => ({
  headerText: "Remove Member",
  bodyText: `Are you sure you want to remove ${userName} from ${workspaceName}?`,
  okButton: {
    text: "Confirm",
    onOk: () => {
      dispatch(
        window.location.hash.includes("/system-admin") ||
          window.location.hash.includes("/cloud-admin")
          ? systemAdminRemoveUserFromWorkspace(
              userName,
              workspaceId,
              workspaceName
            )
          : userAdminRemoveUserFromWorkspace(
              userName,
              workspaceId,
              workspaceName
            )
      );
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const systemAdminDeleteUserModal = (username, dispatch) => ({
  headerText: "Delete User",
  bodyText: `Are you sure you want to delete ${username} from the system? Doing so will remove them from all workspaces they are members in and completely remove them from the system? Do you wish to proceed?`,
  okButton: {
    text: "Confirm",
    onOk: () => {
      dispatch(deleteUser(username));
    },
    cancelButton: {
      text: "Cancel",
      onCancel: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
  },
});

export const systemAdminImportUsersFromCsvConfirmationModal = (
  userResults,
  resultsMessage,
  dispatch
) => ({
  headerText: "Users Imported",
  bodyText: `${resultsMessage}. Click Export Results to get full details.`,
  okButton: {
    text: "Export Results",
    onOk: () => {
      dispatch(exportResultsImportUsersFromCsv(userResults));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const userAdminImportUsersFromCsvConfirmationModal = (
  userResults,
  resultsMessage,
  dispatch,
  isCloudAdmin = false
) => ({
  headerText: "Users Imported",
  bodyText: `${resultsMessage}. Click Export Results to get full details.`,
  okButton: {
    text: "Export Results",
    onOk: () => {
      dispatch(exportResultFromImportUsers(userResults, isCloudAdmin));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const dictionaryTurnOffWildcard = (onOk) => ({
  headerText: "Removing Wildcard?",
  bodyText:
    "You're making this a non-wildcard term. You will not be able to undo this, are you happy to continue?",
  okButton: {
    text: "Yes",
    onOk,
  },
  cancelButton: {
    text: "Cancel",
  },
});

export const licenseSetupConfirmationModal = (dispatch, onClose) => ({
  headerText: "Confirmation",
  bodyText: "We have created the instance. Close to login",
  okButton: {
    text: "OK",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      onClose();
    },
  },
  onClose,
});

export const dictionaryDeleteDictionaryModal = ({
  dictionaryName,
  dictionaryId,
  folders,
  documents,
  navigate,
  dispatch,
}) => ({
  headerText: dictionaryName
    ? `Delete "${dictionaryName}"`
    : "Delete Dictionary",
  bodyText: (() => {
    const amountOfFolders = folders.length;
    const amountOfDocuments = documents.length;
    if (amountOfFolders || amountOfDocuments) {
      let message = "";
      message += `The "${dictionaryName}" dictionary is currently being used by`;
      if (amountOfFolders === 1) {
        message += ` ${amountOfFolders} folder`;
      }
      if (amountOfFolders > 1) {
        message += ` ${amountOfFolders} folders`;
      }
      if (amountOfFolders && amountOfDocuments) {
        message += " and";
      }
      if (amountOfDocuments === 1) {
        message += ` ${amountOfDocuments} document`;
      }
      if (amountOfDocuments > 1) {
        message += ` ${amountOfDocuments} documents`;
      }
      message += ". If you delete this dictionary, the";
      if (amountOfFolders) {
        message += " folders";
      }
      if (amountOfFolders && amountOfDocuments) {
        message += " and";
      }
      if (amountOfDocuments) {
        message += " documents";
      }
      message += " currently using it will be reset to the default dictionary.";
      message += "\n\nThis action cannot be undone. Do you wish to proceed?";
      return message;
    }
    return `"${dictionaryName}" contains some terms and/or categories. This action cannot be undone. Do you wish to proceed?`;
  })(),
  okButton: {
    text: "Yes",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      dispatch(deleteDictionary(dictionaryId, dictionaryName, navigate));
    },
  },
  cancelButton: {
    text: "No",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const dictionarySetAsDefaultModal = (
  dictionaryName,
  dictionaryId,
  dispatch
) => ({
  headerText: "Set as Default Dictionary",
  bodyText: `Are you sure you wish to set ${dictionaryName} as the default dictionary?`,
  okButton: {
    text: "Ok",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      dispatch(setDefaultDictionary(dictionaryId, dictionaryName));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const dictionaryDeleteTermModal = (entry, onOk, dispatch) => {
  const isTerm = has(entry, "dictionaryTermType");
  const headerText = isTerm ? "Term" : "Category";

  return {
    headerText: `Delete ${headerText}`,
    bodyText: !isTerm ? (
      <span>
        Are you sure you want to delete category:{" "}
        <span style={{ fontWeight: "bold" }}>{entry.name}</span>, doing so will
        delete it and its associated terms. Do you wish to continue?
      </span>
    ) : (
      <span>
        Are you sure you want to delete term:{" "}
        <span style={{ fontWeight: "bold" }}>{entry.name}</span>. Do you wish to
        continue?
      </span>
    ),
    okButton: {
      text: "Confirm",
      onOk: () => {
        onOk();
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    cancelButton: {
      text: "Cancel",
      onCancel: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
  };
};

export const permissionsChangedSelfModal = (dispatch) => ({
  headerText: "Permissions Changed",
  bodyText:
    "You have changed permissions on the workspace. We now need to sign you out in order to enforce those changes.",
  okButton: {
    text: "OK",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      dispatch(logout());
    },
  },
});

export const acronymIgnoreListTurnOffRegex = (index, editedItem, dispatch) => ({
  headerText: "Removing Regular Expression?",
  bodyText:
    "You're making this a non-regex term. You will not be able to undo this, are you happy to continue?",
  okButton: {
    text: "Yes",
    onOk: () => {
      dispatch(editItem({ index, editedItem }));
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const changePasswordConfirmationModal = (dispatch) => ({
  headerText: "Change Password",
  bodyText: "Password updated successfully",
  okButton: {
    text: "OK",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const displayNoFilterResults = (filterType) => (dispatch) => {
  const noFilterResultsConfirmationModal = () => ({
    headerText: "No Filter Results",
    bodyText: `We found no results for "${getFilterDescription(filterType)}" filter. Try a different filter`,
    okButton: {
      text: "Confirm",
      onOk: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    onClose: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  });

  dispatch(
    showModal(modalTypes.CONFIRMATION, noFilterResultsConfirmationModal())
  );
};

export const displayNoDocumentsSelected = () => (dispatch) => {
  const noDocumentsSelectedConfirmationModal = () => ({
    headerText: "No Results",
    bodyText:
      "Cannot uncheck document, you must have at least one document checked.",
    okButton: {
      text: "Confirm",
      onOk: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    onClose: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  });

  dispatch(
    showModal(modalTypes.CONFIRMATION, noDocumentsSelectedConfirmationModal())
  );
};

export const displayCannotUncheckReferenceDoc = () => (dispatch) => {
  const cannotUncheckReferenceDocModal = () => ({
    headerText: "No Results",
    bodyText:
      "You cannot uncheck the reference document. Clear the filter and try again.",
    okButton: {
      text: "Confirm",
      onOk: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    onClose: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  });

  dispatch(
    showModal(modalTypes.CONFIRMATION, cannotUncheckReferenceDocModal())
  );
};

export const displayNoSearchResults = (searchTerm) => (dispatch, getState) => {
  const updateSearchTerm = () => {
    // Apply last successful search term
    const prevSearchTerm = getState().discovery.config.searchTerm;
    dispatch(setSearchTerm(null));
    queueMicrotask(() => {
      dispatch(setSearchTerm(prevSearchTerm));
    });
  };

  const noFilterResultsConfirmationModal = () => ({
    headerText: "No Search Results",
    bodyText: `We found no results for "${searchTerm}". Try searching for a different term`,
    okButton: {
      text: "Confirm",
      onOk: () => {
        updateSearchTerm();
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    onClose: () => {
      updateSearchTerm();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  });

  dispatch(
    showModal(modalTypes.CONFIRMATION, noFilterResultsConfirmationModal())
  );
};

export const incorrectNumberOfUsersModal = (dispatch) => ({
  headerText: "Incorrect number of users",
  bodyText:
    "You cannot set a license with a lower number of users than the current active users. Please make some of the users inactive and try again or set a higher user count.",
  okButton: {
    text: "OK",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const deleteCollectionModal = (dispatch, name, onOk) => ({
  headerText: `Delete "${name}"`,
  bodyText: `"${name}" contains documents. This action cannot be undone. Do you wish to proceed?`,
  okButton: {
    text: "Yes",
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "No",
    onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
  },
});

export const deleteDocumentModal = (dispatch, name, onOk) => ({
  headerText: `Delete "${name}"`,
  bodyText: "This action cannot be undone. Do you wish to proceed?",
  okButton: {
    text: "Yes",
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "No",
    onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
  },
});

const removeColumnModal = (dispatch, bodyText, onOk) => ({
  headerText: "Remove Column",
  bodyText,
  okButton: {
    text: "Remove Column",
    variant: NewButtonVariant.red,
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
  },
});

export const shreddingTemplateRemoveColumnModal = (dispatch, name, onOk) =>
  removeColumnModal(dispatch, `Remove “${name}” from this template?`, onOk);

export const shredRemoveColumnModal = (dispatch, name, onOk) =>
  removeColumnModal(
    dispatch,
    `Remove “${name}” from this shred? Its values are deleted when you save.`,
    onOk
  );

export const shredRemoveGroupModal = (dispatch, name, onOk) => ({
  headerText: "Remove Group",
  bodyText: `Remove “${name}” and its columns from this shred? Their values are deleted when you save.`,
  okButton: {
    text: "Remove Group",
    variant: NewButtonVariant.red,
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
  },
});

export const shreddingTemplateRemoveColumnsModal = (dispatch, count, onOk) => ({
  headerText: `Remove ${count} Columns`,
  bodyText: `Remove these ${count} columns from this template? Any group left with fewer than two columns is dropped.`,
  okButton: {
    text: "Remove Columns",
    variant: NewButtonVariant.red,
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
  },
});

export const shreddingTemplateDeleteGroupModal = (
  dispatch,
  name,
  count,
  onOk
) => ({
  headerText: "Delete Group",
  bodyText: `Delete “${name}” and its ${count} columns? To keep the columns and drop only the grouping, use Ungroup instead.`,
  okButton: {
    text: "Delete Group",
    variant: NewButtonVariant.red,
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
  },
});

const describeGroupLosingColumns = ({ name, taking, size }) => {
  const takesAll = taking >= size;
  const count = `${takesAll ? "all " : ""}${taking} ${pluralize("column", taking)}${takesAll ? "" : ` of ${size}`}`;
  const dropped =
    size - taking < 2
      ? ", which leaves too few behind to keep it, so that group goes"
      : "";
  return `“${name}” (${count}${dropped})`;
};

export const shreddingTemplateRegroupModal = (dispatch, groups, onOk) => {
  const many = groups.length > 1 || groups[0]?.taking > 1;
  const subject = many
    ? "Some of the selected columns are"
    : "One of the selected columns is";
  const described = groups.map(describeGroupLosingColumns).join(" and ");
  const pronoun = many ? "them" : "it";
  const from = groups.length > 1 ? "those" : "that one";
  return {
    headerText: "Move Columns Into A New Group",
    bodyText: `${subject} already grouped, in ${described}. A column can only be in one group, so naming a new group takes ${pronoun} out of ${from}. Carry on?`,
    okButton: {
      text: "Move And Continue",
      onOk: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
        onOk();
      },
    },
    cancelButton: {
      text: "Cancel",
      onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
    },
  };
};

export const shreddingTemplateDeleteModal = (dispatch, name, onOk) => ({
  headerText: "Delete Template",
  bodyText: `Delete “${name}”? This is permanent and cannot be undone.`,
  okButton: {
    text: "Delete Template",
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
  },
});

const joinWithAnd = (items) => {
  if (items.length === 0) {
    return "";
  }
  if (items.length === 1) {
    return items[0];
  }
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

const getDeleteOppBodyText = ({
  localDocumentsCount,
  userDocumentsCount,
  reportsCount,
  shredsCount,
  hasProposal,
  actionVerb,
  actionGerund,
}) => {
  if (hasProposal) {
    return `This Opportunity has an associated Proposal. You must delete or Archive the Proposal first before ${actionGerund}.`;
  }

  const warningText = `By ${actionGerund}, we will remove all this data. This action cannot be undone. Do you still want to ${actionVerb}?`;

  const additionalDeps = [];
  if (shredsCount > 0) {
    additionalDeps.push(`${shredsCount} ${pluralize("shred", shredsCount)}`);
  }
  if (reportsCount > 0) {
    additionalDeps.push(
      `${reportsCount} amendment ${pluralize("report", reportsCount)}`
    );
  }
  if (userDocumentsCount > 0) {
    additionalDeps.push(
      `${userDocumentsCount} user created ${pluralize("doc", userDocumentsCount)}`
    );
  }

  let message = "";
  if (localDocumentsCount > 0) {
    message = `This Opportunity has ${localDocumentsCount} ${pluralize("document", localDocumentsCount)} associated with it. `;

    if (additionalDeps.length > 0) {
      message += ` It also has ${joinWithAnd(additionalDeps)}.`;
    }

    message += ` ${warningText}`;
  } else if (additionalDeps.length > 0) {
    message = `This Opportunity has ${joinWithAnd(additionalDeps)}. ${warningText}`;
  }

  return message;
};

export const deleteOppWithDependencies = (
  onOk,
  onCancel,
  isManualOpp,
  localDocumentsCount,
  userDocumentsCount,
  reportsCount,
  shredsCount,
  hasProposal,
  opportunityTitle
) => {
  const actionVerb = isManualOpp ? "Delete" : "Untrack";
  const actionGerund = isManualOpp ? "deleting" : "Untracking";

  const bodyText = getDeleteOppBodyText({
    localDocumentsCount,
    userDocumentsCount,
    reportsCount,
    shredsCount,
    hasProposal,
    actionVerb,
    actionGerund,
  });

  if (!bodyText) {
    onOk();
    return null;
  }

  return {
    headerText: `${actionVerb} Opportunity - ${opportunityTitle}`,
    bodyText,
    okButton: {
      text: hasProposal ? "Ok" : `Yes, ${actionVerb} this Opp`,
      onOk: () => {
        hasProposal ? onCancel() : onOk();
      },
    },
    cancelButton: {
      text: "Cancel",
      onCancel,
    },
  };
};

const getDeleteProposalBodyText = ({
  localDocumentCount,
  userCreatedDocs,
  amendmentReportCount,
  shredCount,
}) => {
  const warningText =
    "By deleting, we will remove all this data. This action cannot be undone. Do you still want to Delete? ";

  const additionalDeps = [];
  if (shredCount > 0) {
    additionalDeps.push(`${shredCount} ${pluralize("shred", shredCount)}`);
  }
  if (amendmentReportCount > 0) {
    additionalDeps.push(
      `${amendmentReportCount} amendment ${pluralize("report", amendmentReportCount)}`
    );
  }
  if (userCreatedDocs > 0) {
    additionalDeps.push(
      `${userCreatedDocs} user created ${pluralize("doc", userCreatedDocs)}`
    );
  }

  let message = "";
  if (localDocumentCount > 0) {
    message = `This Proposal has ${localDocumentCount} ${pluralize("document", localDocumentCount)} associated with it.`;

    if (additionalDeps.length > 0) {
      message += ` It also has ${joinWithAnd(additionalDeps)}.`;
    }

    message += ` ${warningText}`;
  } else if (additionalDeps.length > 0) {
    message = `This Proposal has ${joinWithAnd(additionalDeps)}. ${warningText}`;
  }

  return message;
};

export const deleteProposalWithDependencies = (
  dispatch,
  onOk,
  localDocumentCount,
  userCreatedDocs,
  amendmentReportCount,
  shredCount,
  proposalTitle
) => {
  const bodyText = getDeleteProposalBodyText({
    localDocumentCount,
    userCreatedDocs,
    amendmentReportCount,
    shredCount,
  });

  if (!bodyText) {
    onOk();
    return null;
  }

  return {
    headerText: `Delete Proposal - ${proposalTitle}`,
    bodyText,
    okButton: {
      text: "Yes, Delete this Proposal",
      onOk,
    },
    cancelButton: {
      text: "Cancel",
      onCancel: () => dispatch(hideModal(modalTypes.CONFIRMATION)),
    },
  };
};

export const confirmTypeSpecificFieldsReset = (onOk, dispatch) => ({
  headerText: "Switching Opp Type",
  bodyText:
    "If you change the Opp Type, you will lose any data specific to that type.",
  okButton: {
    text: "Continue",
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const regenerateOutlineConfirmationModal = (
  onConfirm,
  dispatch,
  commentCount
) => ({
  headerText: "Confirmation",
  bodyText:
    "You have an existing outline.\n\nBy regenerating, we will replace this outline in total, along with all data." +
    (commentCount > 0
      ? ` This will also delete ${commentCount} comment${
          commentCount === 1 ? "" : "s"
        } left on its sections.`
      : "") +
    " Do you still want to regenerate?",
  okButton: {
    text: "Continue",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      onConfirm();
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  onClose: () => {
    dispatch(hideModal(modalTypes.CONFIRMATION));
  },
});

export const confirmPromptLibraryOverwrite = (onOk, dispatch) => ({
  headerText: "Overwrite Prompt Library",
  bodyText:
    "This will replace all your editable prompts and categories with the contents of the imported file. This action cannot be undone.",
  okButton: {
    text: "Overwrite",
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

const getAffectedUsersLabel = (isOnPremise) =>
  isOnPremise ? "all users of this instance" : "all users of this workspace";

const disableAiSettingModal = ({ dispatch, headerText, impactText, onOk }) => ({
  headerText,
  bodyText: `${impactText}\n\nDo you wish to proceed?`,
  okButton: {
    text: "Confirm",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      onOk();
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  onClose: () => {
    dispatch(hideModal(modalTypes.CONFIRMATION));
  },
});

export const disableGenerativeAiModal = (
  dispatch,
  isOnPremise,
  onOk,
  includesVisiEverywhere = false
) =>
  disableAiSettingModal({
    dispatch,
    headerText: "Disable Generative AI Features",
    impactText: `Turning off Generative AI features will disable ${
      includesVisiEverywhere
        ? "Visi Chat, Collections and Visi Everywhere"
        : "Visi Chat and Collections"
    } for ${getAffectedUsersLabel(isOnPremise)}.`,
    onOk,
  });

export const disableCollectionsModal = (dispatch, isOnPremise, onOk) =>
  disableAiSettingModal({
    dispatch,
    headerText: "Disable Collections",
    impactText: `Turning off Collections will make all existing collections unavailable to ${getAffectedUsersLabel(isOnPremise)}.`,
    onOk,
  });

export const disableLocalFileSharingModal = (dispatch, isOnPremise, onOk) =>
  disableAiSettingModal({
    dispatch,
    headerText: "Disable Local File Sharing",
    impactText: `Turning off sharing of Collections containing local files will stop ${getAffectedUsersLabel(isOnPremise)} from sharing those collections.`,
    onOk,
  });

export const goToOpportunitySearchModal = (
  dispatch,
  navigate,
  onAddManually,
  returnModalType = modalTypes.NEW_OPPORTUNITY_CHOOSER
) => ({
  headerText: "Go to Opportunity Search?",
  bodyText:
    "You'll leave this page and navigate to Opportunity Search, where you can find and track new opportunities and start a proposal.",
  okButton: {
    text: "Go to Opp Search",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      navigate("/capture/opportunities");
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(showModal(returnModalType, { onAddManually }));
    },
  },
  onClose: () => {
    dispatch(hideModal(modalTypes.CONFIRMATION));
  },
});
